/**
 * New Mega Menu JavaScript
 * Handles desktop hover mega menu and mobile sliding drawer functionality
 * Replicates behavior from index.html
 */

class MegaMenuNew {
  constructor() {
    this.nav = document.querySelector('.mega-menu-new-nav');
    this.mobileDrawer = document.querySelector('.mega-menu-new-mobile-drawer');
    this.mobileOverlay = document.querySelector('.mega-menu-new-mobile-overlay');
    this.backButton = document.querySelector('.mega-menu-new-back-button');
    this.backText = document.querySelector('.mega-menu-new-back-text');
    this.drawerMain = document.querySelector('.mega-menu-new-drawer-list');
    this.drawerSub = document.querySelector('.mega-menu-new-drawer-sublist');
    this.submenuContainer = document.querySelector('.mega-menu-new-submenu-container');
    this.submenuBackButton = document.querySelector('.mega-menu-new-submenu-back-button');
    this.submenuTitle = document.getElementById('megaMenuSubmenuTitle');

    this.isDesktop = window.innerWidth >= 1024;
    this.currentLevel = 'main';

    // Desktop hover state tracking
    this.activePanel = null;
    this.desktopOpen = false;
    this.hoverDelay = 80; // ms – grace period for cursor movement
    this.closeDelay = 150; // ms – delay before closing when leaving menu area
    this.switchDelay = 50; // ms – delay when switching between menu items

    // Cursor tracking for smart hover detection
    this.lastMousePosition = { x: 0, y: 0 };
    this.mouseMovingToMenu = false;
    this.closeTimeout = null;
    this.switchTimeout = null;

    if (this.nav) {
      this.init();
    }
  }

  init() {
    if (!this.nav) return;

    this.setupHeaderHeight();
    this.setupDesktopHover();
    this.setupMobileMenu();
    this.setupResponsive();
    this.setupAccessibility();
  }

  setupHeaderHeight() {
    // Calculate and set header height for mobile drawer positioning
    const updateHeaderHeight = () => {
      const header = document.querySelector('.shopify-section-group-header-group') ||
        document.querySelector('.wheelers-header') ||
        document.querySelector('header');

      if (header) {
        const headerHeight = header.offsetHeight;
        document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
      }
    };

    window.updateHeaderHeight = updateHeaderHeight;

    // Set initial height
    updateHeaderHeight();

    // Update on resize
    window.addEventListener('resize', updateHeaderHeight);

    // Update after fonts load
    document.addEventListener('DOMContentLoaded', updateHeaderHeight);
    window.addEventListener('load', updateHeaderHeight);
  }

  setupDesktopHover() {
    const megaItems = document.querySelectorAll('.mega-menu-new-item-with-mega');

    // Track mouse movement for smart hover detection
    document.addEventListener('mousemove', (e) => {
      this.lastMousePosition = { x: e.clientX, y: e.clientY };
    });

    const openFor = (panel) => {
      if (!panel) return;

      // Clear any pending close/switch timeouts
      clearTimeout(this.closeTimeout);
      clearTimeout(this.switchTimeout);

      // Show overlay once
      if (!this.desktopOpen) {
        this.showOverlay();
        this.desktopOpen = true;
      }

      // Handle panel switching - no delays to prevent jumping
      if (this.activePanel && this.activePanel !== panel) {
        this.hideMegaMenuPanel(this.activePanel, true);
        this.showMegaMenu(panel, true);
        this.activePanel = panel;
      } else {
        this.showMegaMenu(panel, true);
        this.activePanel = panel;
      }
    };

    megaItems.forEach((item) => {
      const panel = item.querySelector('.mega-menu-new-panel');

      item.addEventListener('mouseenter', () => {
        if (window.innerWidth >= 1024 && panel) {
          openFor(panel, false);
        }
      });

      item.addEventListener('mouseleave', () => {
        if (window.innerWidth >= 1024) {
          // Shorter delay to allow moving to panel
          setTimeout(() => {
            if (!this.isHoveringMegaMenuArea()) {
              this.hideAllMegaMenus();
              this.desktopOpen = false;
              this.activePanel = null;
            }
          }, 50); // Reduced from 80ms to 50ms
        }
      });

      if (panel) {
        panel.addEventListener('mouseenter', () => {
          clearTimeout(this.closeTimeout);
          clearTimeout(this.switchTimeout);
        });

        panel.addEventListener('mouseleave', () => {
          // Small delay to allow hover states to update; then close unless over another item/panel
          setTimeout(() => {
            if (!this.isMovingToAnotherMenuItem() && !this.isHoveringMegaMenuArea()) {
              this.hideAllMegaMenus();
              this.desktopOpen = false;
              this.activePanel = null;
            }
          }, 50);
        });
      }
    });
  }

  isMovingToAnotherMenuItem() {
    // Check if cursor is over any menu item (not the panels)
    const megaItems = document.querySelectorAll('.mega-menu-new-item-with-mega');
    for (let item of megaItems) {
      if (item.matches(':hover')) {
        return true;
      }
    }
    return false;
  }

  isHoveringMegaMenuArea() {
    const megaItems = document.querySelectorAll('.mega-menu-new-item-with-mega');
    const panels = document.querySelectorAll('.mega-menu-new-panel');

    // Check if hovering over any menu item
    for (let item of megaItems) {
      if (item.matches(':hover')) return true;
    }

    // Check if hovering over any panel
    for (let panel of panels) {
      if (panel.matches(':hover')) return true;
    }

    // Check if hovering over the main navigation area
    const nav = document.querySelector('.mega-menu-new-nav');
    if (nav && nav.matches(':hover')) return true;

    return false;
  }

  showMegaMenu(panel, instant = false) {
    if (!this.desktopOpen) {
      this.showOverlay();
      this.desktopOpen = true;
    }

    const prev = this.activePanel;
    if (prev && prev !== panel) {
      // Show the incoming panel first, immediately
      panel.style.transition = 'none';
      panel.style.opacity = '1';
      panel.style.visibility = 'visible';
      panel.style.transform = 'translateY(0)';
      panel.classList.add('mega-menu-active');

      // Hide the previous panel on the next frame to avoid flash
      requestAnimationFrame(() => {
        prev.style.transition = 'none';
        prev.style.opacity = '0';
        prev.style.visibility = 'hidden';
        prev.style.transform = 'translateY(-10px)';
        prev.classList.remove('mega-menu-active');

        // Re-enable transitions after
        requestAnimationFrame(() => {
          panel.style.transition = '';
          prev.style.transition = '';
        });
      });

      this.activePanel = panel;
      return;
    }

    // No previous panel or same panel
    panel.style.transition = 'none';

    // Hide all other panels instantly
    const allPanels = document.querySelectorAll('.mega-menu-new-panel');
    allPanels.forEach(p => {
      if (p !== panel) {
        p.style.transition = 'none';
        p.style.opacity = '0';
        p.style.visibility = 'hidden';
        p.classList.remove('mega-menu-active');
      }
    });

    // Show target panel instantly
    panel.style.opacity = '1';
    panel.style.visibility = 'visible';
    panel.style.transform = 'translateY(0)';
    panel.classList.add('mega-menu-active');

    // Re-enable transitions after a frame
    requestAnimationFrame(() => {
      panel.style.transition = '';
      allPanels.forEach(p => { if (p !== panel) { p.style.transition = ''; } });
    });

    this.activePanel = panel;
  }

  hideMegaMenu(panel) {
    this.hideMegaMenuPanel(panel, /*instant*/ true);
    this.hideOverlay();
    this.desktopOpen = false;
    this.activePanel = null;
  }

  hideMegaMenuPanel(panel, instant = false) {
    if (!panel) return;
    if (instant) {
      panel.style.transition = 'none';
    } else {
      panel.style.transition = '';
    }
    panel.style.opacity = '0';
    panel.style.visibility = 'hidden';
    panel.style.transform = 'translateY(-10px)';
    panel.classList.remove('mega-menu-active');
  }

  // Show the page overlay (desktop). Use the existing overlay element from the snippet.
  showOverlay() {
    const overlay = document.getElementById('megaMenuDesktopOverlay') || document.querySelector('.mega-menu-new-overlay');
    if (overlay) {
      overlay.classList.add('is-active');
      overlay.addEventListener('click', () => this.hideAllMegaMenus(), { once: true });
    }
  }

  // Hide the page overlay
  hideOverlay() {
    const overlay = document.getElementById('megaMenuDesktopOverlay') || document.querySelector('.mega-menu-new-overlay');
    if (overlay) {
      overlay.classList.remove('is-active');
    }
  }

  hideAllMegaMenus() {
    // Clear all timeouts to prevent conflicts
    clearTimeout(this.closeTimeout);
    clearTimeout(this.switchTimeout);

    document.querySelectorAll('.mega-menu-new-panel').forEach(panel => {
      this.hideMegaMenuPanel(panel, /*instant*/ true);
    });
    this.hideOverlay();
    this.desktopOpen = false;
    this.activePanel = null;
  }

  // Enhanced cursor movement detection
  isMovingTowardsMenu(currentX, currentY) {
    const nav = document.querySelector('.mega-menu-new-nav');
    if (!nav) return false;

    const navRect = nav.getBoundingClientRect();
    const prevX = this.lastMousePosition.x;
    const prevY = this.lastMousePosition.y;

    // Check if cursor is moving towards the navigation area
    const movingRight = currentX > prevX && currentX < navRect.right;
    const movingLeft = currentX < prevX && currentX > navRect.left;
    const movingDown = currentY > prevY && currentY < navRect.bottom + 100; // 100px grace area

    return (movingRight || movingLeft) && movingDown;
  }

  setupMobileMenu() {
    if (!this.mobileDrawer) return;

    // Build mobile menu from desktop menu (delay to ensure DOM is fully rendered)
    setTimeout(() => this.buildMobileMenu(), 0);

    // Main header back button - only closes drawer when at main level
    if (this.backButton) {
      this.backButton.addEventListener('click', () => {
        this.handleMainBackButtonClick();
      });
    }

    // Submenu back button - returns to main menu
    if (this.submenuBackButton) {
      this.submenuBackButton.addEventListener('click', () => {
        this.goBackInMobileMenu();
      });
    }

    // Submenu close button - closes entire menu
    const submenuCloseButton = document.querySelector('.mega-menu-new-submenu-close');
    if (submenuCloseButton) {
      submenuCloseButton.addEventListener('click', () => {
        this.closeMobileMenu();
      });
    }

    // Overlay click
    if (this.mobileOverlay) {
      this.mobileOverlay.addEventListener('click', () => {
        this.closeMobileMenu();
      });
    }

    // Listen for mobile menu trigger from existing header
    const mobileToggle = document.querySelector('.mobile-menu-toggle');

    if (mobileToggle && this.mobileDrawer) {
      mobileToggle.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.openMobileMenu();
      });
    }
  }

  buildMobileMenu() {
    if (!this.drawerMain) {
      console.warn('[MegaMenuNew] drawerMain not found when building mobile menu');
      return;
    }

    // Clear existing content to prevent duplication
    this.drawerMain.innerHTML = '';
    console.log('[MegaMenuNew] Building hardcoded mobile menu');

    // HARDCODED MOBILE MENU ITEMS - matches desktop navigation order
    const hardcodedMenuItems = [
      { title: 'Brands', handle: 'brands' },
      { title: 'Pens', handle: 'pens' },
      { title: 'Stationery & Refills', handle: 'stationery-refills' },
      { title: 'Leather & Travel', handle: 'leather-travel' },
      { title: 'Jewellery', handle: 'jewellery' },
      { title: 'Watches', handle: 'watches' },
      { title: 'Accessories', handle: 'accessories' },
      { title: 'Homeware', handle: 'homeware' },
      { title: 'Clothing', handle: 'clothing' },
      { title: 'Alcohol & Hampers', handle: 'alcohol-hampers' }
    ];

    hardcodedMenuItems.forEach(menuItem => {
      // Find corresponding desktop panel
      const desktopPanel = document.querySelector(`[data-mega-menu="${menuItem.handle}"]`);

      const mobileItem = document.createElement('li');
      mobileItem.classList.add('mega-menu-new-drawer-link-item');

      if (desktopPanel) {
        // Has mega menu
        mobileItem.innerHTML = `
          <span class="mega-menu-new-drawer-main-link">${menuItem.title}</span>
          <span class="mega-menu-new-drawer-arrow">&gt;</span>
        `;

        mobileItem.addEventListener('click', () => {
          this.showMobileSubmenu(menuItem, desktopPanel);
        });
      } else {
        // Regular link (fallback)
        const collectionUrl = `/collections/${menuItem.handle}`;
        mobileItem.innerHTML = `
          <a href="${collectionUrl}" class="mega-menu-new-drawer-main-link" style="text-decoration: none; color: inherit; width: 100%;">
            ${menuItem.title}
          </a>
        `;
      }

      this.drawerMain.appendChild(mobileItem);
      console.log('[MegaMenuNew] Added hardcoded item:', menuItem.title);
    });

    console.log('[MegaMenuNew] Hardcoded mobile menu built with', hardcodedMenuItems.length, 'items');
  }

  showMobileSubmenu(menuItem, panel) {
    const title = menuItem && menuItem.title ? menuItem.title : '';
    this.currentLevel = 'sub';

    if (!this.drawerSub) return;

    // Update back button text to show "Back"
    this.updateBackButtonText('');

    const parentItem = panel ? panel.closest('.mega-menu-new-item-with-mega') : null;
    const parentLinkElement = parentItem ? parentItem.querySelector('.mega-menu-new-link') : null;
    const parentLinkText = parentLinkElement && parentLinkElement.textContent
      ? parentLinkElement.textContent.trim()
      : '';
    const parentLinkHrefAttr = parentLinkElement ? parentLinkElement.getAttribute('href') : null;
    const parentLinkHref = parentLinkHrefAttr && parentLinkHrefAttr !== '#' ? parentLinkHrefAttr : null;
    const fallbackHref = menuItem && menuItem.href
      ? menuItem.href
      : (menuItem && menuItem.handle ? `/collections/${menuItem.handle}` : null);
    const resolvedTitle = parentLinkText || title;
    const resolvedHref = parentLinkHref || fallbackHref || null;
    console.log('[MegaMenuNew] Showing mobile submenu for:', resolvedTitle);

    // Build submenu content
    const columns = panel.querySelectorAll('.mega-menu-new-column');
    const collectionsGrid = panel.querySelector('.mega-menu-new-collections-grid');
    let submenuHTML = ``;

    // Handle collections grid (like Brands) - check at panel level first
    if (collectionsGrid) {
      const collectionItems = collectionsGrid.querySelectorAll('.mega-collection-item');
      collectionItems.forEach(item => {
        const link = item.querySelector('a');
        const title = item.querySelector('.mega-collection-title');
        if (link && title) {
          submenuHTML += `<li><a href="${link.href}" style="color: inherit; text-decoration: none; display: block; padding: 0.25rem 0 0.25rem 1rem;">${title.textContent}</a></li>`;
        }
      });
    }

    // Handle regular column structure
    columns.forEach(column => {
      const heading = column.querySelector('.mega-menu-new-heading');
      const links = column.querySelectorAll('.mega-menu-new-sublink');
      const columnCollectionsGrid = column.querySelector('.mega-menu-new-collections-grid');

      if (heading) {
        const headingLink = heading.querySelector('a');
        if (headingLink) {
          submenuHTML += `<li><a href="${headingLink.href}" style="font-weight: bold; color: inherit; text-decoration: none; display: block; padding: 0.5rem 0;">${heading.textContent}</a></li>`;
        } else {
          submenuHTML += `<li style="font-weight: bold; padding: 0.5rem 0;">${heading.textContent}</li>`;
        }
      }

      // Handle regular text links
      links.forEach(link => {
        submenuHTML += `<li><a href="${link.href}" style="color: inherit; text-decoration: none; display: block; padding: 0.25rem 0 0.25rem 1rem;">${link.textContent}</a></li>`;
      });

      // Handle collection images within columns - show as text links on mobile
      if (columnCollectionsGrid) {
        const collectionItems = columnCollectionsGrid.querySelectorAll('.mega-collection-item');
        collectionItems.forEach(item => {
          const link = item.querySelector('a');
          const title = item.querySelector('.mega-collection-title');
          if (link && title) {
            submenuHTML += `<li><a href="${link.href}" style="color: inherit; text-decoration: none; display: block; padding: 0.25rem 0 0.25rem 1rem;">${title.textContent}</a></li>`;
          }
        });
      }
    });

    // Show submenu container and populate content
    if (this.submenuTitle) {
      this.submenuTitle.textContent = resolvedTitle || '';
      if (resolvedHref) {
        this.submenuTitle.setAttribute('href', resolvedHref);
        this.submenuTitle.removeAttribute('aria-disabled');
      } else {
        this.submenuTitle.removeAttribute('href');
        this.submenuTitle.setAttribute('aria-disabled', 'true');
      }
    }
    this.drawerSub.innerHTML = submenuHTML;
    this.drawerSub.scrollTop = 0;
    if (this.submenuContainer) {
      this.submenuContainer.style.display = 'block';
      this.submenuContainer.classList.remove('mega-menu-new-slide-out');
      this.submenuContainer.classList.add('mega-menu-new-slide-in');
    }
    this.drawerMain.style.display = 'none';
  }

  handleMainBackButtonClick() {
    // Main header back button only closes drawer (submenu has its own back button)
    this.closeMobileMenu();
  }

  goBackInMobileMenu() {
    if (this.currentLevel === 'sub' && this.submenuContainer) {
      // Use slide-right animation
      this.submenuContainer.classList.remove('mega-menu-new-slide-in');
      this.submenuContainer.classList.add('mega-menu-new-slide-out');

      setTimeout(() => {
        this.submenuContainer.style.display = 'none';
        this.submenuContainer.classList.remove('mega-menu-new-slide-out');
        this.drawerMain.style.display = 'block';
        this.currentLevel = 'main';
        if (this.submenuTitle) {
          this.submenuTitle.textContent = '';
          this.submenuTitle.removeAttribute('href');
          this.submenuTitle.setAttribute('aria-disabled', 'true');
        }
        if (this.drawerSub) {
          this.drawerSub.innerHTML = '';
          this.drawerSub.scrollTop = 0;
        }
      }, 300);
    }
  }

  openMobileMenu() {
    if (this.mobileDrawer && this.mobileOverlay) {
      // Rebuild on open to capture late-rendered items (e.g., Brands)
      this.buildMobileMenu();

      this.mobileDrawer.classList.add('mega-menu-new-open');
      this.mobileOverlay.style.display = 'block';
      document.documentElement.classList.add('mega-menu-new-scroll-lock');
      document.body.classList.add('mega-menu-new-scroll-lock');

      // Prevent background scroll on iOS
      this._touchMoveHandler = (e) => {
        if (!this.mobileDrawer.contains(e.target)) {
          e.preventDefault();
        }
      };
      document.addEventListener('touchmove', this._touchMoveHandler, { passive: false });

      // Ensure we start at main level
      this.currentLevel = 'main';
      if (this.drawerMain) this.drawerMain.style.display = 'block';
      if (this.submenuContainer) this.submenuContainer.style.display = 'none';
      this.updateBackButtonText('Back');
    }
  }

  closeMobileMenu() {
    if (this.mobileDrawer && this.mobileOverlay) {
      this.mobileDrawer.classList.remove('mega-menu-new-open');
      this.mobileOverlay.style.display = 'none';
      document.documentElement.classList.remove('mega-menu-new-scroll-lock');
      document.body.classList.remove('mega-menu-new-scroll-lock');
      if (this._touchMoveHandler) {
        document.removeEventListener('touchmove', this._touchMoveHandler);
        this._touchMoveHandler = null;
      }

      // Reset to main level and hide submenu container
      if (this.drawerMain) this.drawerMain.style.display = 'block';
      if (this.submenuContainer) this.submenuContainer.style.display = 'none';
      this.currentLevel = 'main';
      this.updateBackButtonText('Back');
      if (this.submenuTitle) {
        this.submenuTitle.textContent = '';
        this.submenuTitle.removeAttribute('href');
        this.submenuTitle.setAttribute('aria-disabled', 'true');
      }
      if (this.drawerSub) {
        this.drawerSub.innerHTML = '';
        this.drawerSub.scrollTop = 0;
      }
    }
  }

  updateBackButtonText(text) {
    if (this.backText) {
      this.backText.textContent = text;
    }
  }



  setupResponsive() {
    window.addEventListener('resize', () => {
      this.isDesktop = window.innerWidth >= 1024;

      if (this.isDesktop) {
        this.closeMobileMenu();
        if (!this.isHoveringMegaMenuArea()) {
          this.hideAllMegaMenus();
        }
      }
    });
  }

  setupAccessibility() {
    // Keyboard navigation for desktop
    const megaItems = document.querySelectorAll('.mega-menu-new-item-with-mega');

    megaItems.forEach(item => {
      const link = item.querySelector('.mega-menu-new-link');
      const panel = item.querySelector('.mega-menu-new-panel');

      if (link && panel) {
        link.addEventListener('focus', () => {
          if (window.innerWidth >= 1024) {
            this.showMegaMenu(panel);
          }
        });

        // Close on escape
        link.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && window.innerWidth >= 1024) {
            this.hideMegaMenu(panel);
            link.blur();
          }
        });
      }
    });

    // Mobile menu keyboard navigation
    if (this.backButton) {
      this.backButton.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.closeMobileMenu();
        }
      });
    }
  }
}

// Initialize when DOM is loaded
function __initMegaMenuNew() {
  window.__megaMenuNewInstance = new MegaMenuNew();
}

document.addEventListener('DOMContentLoaded', __initMegaMenuNew);

window.addEventListener('load', () => {
  // Ensure updateHeaderHeight exists before calling
  if (typeof window.updateHeaderHeight === 'function') {
    window.updateHeaderHeight();
  } else {
    console.warn('[MegaMenuNew] updateHeaderHeight is not defined at load.');
  }
});

// Ensure overlay is hidden on navigation/page visibility events
(function () {
  const hideOverlayNow = () => {
    const overlay = document.getElementById('megaMenuDesktopOverlay') || document.querySelector('.mega-menu-new-overlay');
    if (overlay) overlay.classList.remove('is-active');
    const nav = document.querySelector('.mega-menu-new-nav');
    // Also hide any active panels if script instance exists
    if (window.__megaMenuNewInstance && typeof window.__megaMenuNewInstance.hideAllMegaMenus === 'function') {
      window.__megaMenuNewInstance.hideAllMegaMenus();
    } else if (nav) {
      // Fallback: hide panels by class
      document.querySelectorAll('.mega-menu-new-panel').forEach(p => {
        p.style.opacity = '0';
        p.style.visibility = 'hidden';
        p.classList.remove('mega-menu-active');
      });
    }
  };

  // Hide on page show (back/forward cache), before unload, and visibility change
  window.addEventListener('pageshow', hideOverlayNow);
  window.addEventListener('pagehide', hideOverlayNow);
  window.addEventListener('beforeunload', hideOverlayNow);
  document.addEventListener('visibilitychange', () => { if (document.visibilityState !== 'visible') hideOverlayNow(); });

  // Hide on navigation link clicks within the mega menu
  document.addEventListener('click', (e) => {
    const anchor = e.target.closest('a');
    if (!anchor) return;
    if (anchor.closest('.mega-menu-new-panel') || anchor.closest('.mega-menu-new-nav')) {
      hideOverlayNow();
    }
  }, true);
})();
